import { useState, useEffect, useRef } from 'react'
import { Printer, FileText, Download, X } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import leiloesService from '../../services/leiloesService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useSidebar } from '../../components/layout/Layout'

export default function FichaCurral() {
  const [leiloes, setLeiloes] = useState([])
  const [selectedLeilao, setSelectedLeilao] = useState('')
  const [loading, setLoading] = useState(true)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)
  const [gerando, setGerando] = useState(false)
  const [fichasPorFolha, setFichasPorFolha] = useState(3)
  const iframeRef = useRef(null)
  const { organizadorAtivo } = useOrganizador()
  const { setSidebarOpen } = useSidebar()

  useEffect(() => {
    if (organizadorAtivo?.id) loadLeiloes()
  }, [organizadorAtivo])

  const loadLeiloes = async () => {
    try {
      setLoading(true)
      const response = await leiloesService.getByOrganizador(organizadorAtivo.id, { per_page: 100 })
      const leiloesData = response || []
      setLeiloes(leiloesData)
      if (leiloesData.length > 0) {
        setSelectedLeilao((leiloesData.find(l => l.status === 'on') || leiloesData[0]).id.toString())
      }
    } catch (error) {
      console.error('Erro ao carregar leilões:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGerar = async () => {
    if (!selectedLeilao || !organizadorAtivo?.id) return
    setGerando(true)
    try {
      const { default: api } = await import('../../api/client')
      const response = await api.get(
        `/organizadores/${organizadorAtivo.id}/catalogos/leiloes/${selectedLeilao}/ficha-curral?fichas_por_folha=${fichasPorFolha}`,
        { responseType: 'blob', timeout: 60000 }
      )
      const blob = new Blob([response.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(URL.createObjectURL(blob))
      setShowPdfModal(true)
    } catch (err) {
      alert('Erro ao gerar ficha do curral: ' + (err.response?.statusText || err.message))
    } finally {
      setGerando(false)
    }
  }

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const handleDownload = () => {
    if (!pdfUrl) return
    const leilao = leiloes.find(l => String(l.id) === selectedLeilao)
    downloadPdf(pdfUrl, `Ficha do Curral - ${leilao?.nome || selectedLeilao}.pdf`.replace(/[/\\?%*:|"<>]/g, '-'))
  }

  const leilaoAtual = leiloes.find(l => String(l.id) === selectedLeilao)

  if (!organizadorAtivo?.id) return (
    <div className="p-6 text-center text-gray-400">Selecione um organizador</div>
  )

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Ficha do Curral</h1>
            <p className="text-teal-100 text-sm mt-1">Fichas simplificadas para identificação no curral</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[240px]">
            <label className="text-xs font-medium text-gray-500">Leilão</label>
            <select
              value={selectedLeilao}
              onChange={e => setSelectedLeilao(e.target.value)}
              disabled={loading}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            >
              <option value="">Selecione um leilão</option>
              {leiloes.map(l => (
                <option key={l.id} value={String(l.id)}>{l.nome}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Fichas por folha</label>
            <select
              value={fichasPorFolha}
              onChange={e => setFichasPorFolha(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            >
              <option value={1}>1 por folha</option>
              <option value={2}>2 por folha</option>
              <option value={3}>3 por folha</option>
              <option value={4}>4 por folha</option>
            </select>
          </div>

          <button
            onClick={handleGerar}
            disabled={!selectedLeilao || gerando}
            className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Printer size={16} />
            {gerando ? 'Gerando...' : 'Gerar PDF'}
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-600 space-y-1">
            <p className="font-medium text-gray-700">Sobre a Ficha do Curral</p>
            <p>Cada ficha contém: número do lote, sexo, quantidade, peso e observações.</p>
            <p>Ideal para fixar nos currais de identificação dos lotes durante o evento.</p>
          </div>
        </div>
      </div>

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col" style={{ height: '92vh' }}>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-teal-100 rounded-lg flex-shrink-0">
                  <FileText size={20} className="text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-semibold text-gray-900">Ficha do Curral</h2>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{leilaoAtual?.nome || `Leilão #${selectedLeilao}`}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={handleDownload} disabled={!pdfUrl}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium">
                  <Download size={15} /> <span className="hidden sm:inline">Baixar </span>PDF
                </button>
                <button onClick={printPdf} disabled={!pdfUrl}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium">
                  <Printer size={15} /> Imprimir
                </button>
                <button onClick={closePdfModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X size={18} className="text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Ficha do Curral"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
